import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { CheckCircle2, Circle, ChevronRight, UserPlus, Package, ClipboardList, ShieldCheck, FileDown, ArrowLeft, Trash2, X, Camera, MessageSquare, TrendingUp, TrendingDown, AlertTriangle, Activity, Settings2 } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import ProjectChatPanel from "@/components/ProjectChatPanel";
import { fmtMoney } from "@/lib/money";
import { getToken } from "@/lib/session";

const STAGE_LABELS = {
  ordered: "Ordered",
  in_process: "In Process",
  awaiting_arrival: "Awaiting Materials",
  scheduling: "Scheduling",
  scheduled: "Scheduled",
  installed: "Installed",
  completed: "Completed",
};
const STAGE_SEQ = ["ordered","in_process","awaiting_arrival","scheduling","scheduled","installed","completed"];

export default function AdminProjectDetail() {
  const { id } = useParams();
  const [proj, setProj] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [punchlist, setPunchlist] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [warranty, setWarranty] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  const [showPunch, setShowPunch] = useState(false);
  const [photoPunch, setPhotoPunch] = useState(null);

  const load = async () => {
    try {
      const [p, cl, pl, mats, w] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/checklist`),
        api.get(`/projects/${id}/punchlist`),
        api.get(`/materials`).catch(() => ({ data: [] })),
        api.get(`/projects/${id}/warranty`).catch(() => ({ data: null })),
      ]);
      setProj(p.data);
      setChecklist(cl.data.items || []);
      setPunchlist(pl.data || []);
      setMaterials(mats.data || []);
      setWarranty(w.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const tick = async (key, done) => {
    try {
      const r = await api.post(`/projects/${id}/checklist/${key}`, { done });
      setChecklist(r.data.items);
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const advance = async () => {
    try {
      await api.post(`/projects/${id}/advance`);
      toast.success("Advanced to next stage");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object") {
        if (d.error === "checklist_incomplete") toast.error(`Missing: ${d.unmet.join(", ")}`);
        else if (d.error === "no_crew_assigned") toast.error("Assign a crew member first");
        else if (d.error === "no_materials_reserved") toast.error("Reserve materials first");
        else if (d.error === "punchlist_open") toast.error(`Close all ${d.count} punchlist item(s) first`);
        else toast.error(d.detail || d.error || "Could not advance stage — please review the checklist and try again.");
      } else toast.error(d || "Advance failed");
    }
  };

  const releaseMat = async (rid) => {
    try {
      await api.delete(`/projects/${id}/materials/${rid}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const removeCrew = async (uid) => {
    try {
      await api.delete(`/projects/${id}/crew/${uid}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const closePunch = async (pid) => {
    try {
      await api.patch(`/punchlist/${pid}`, { status: "closed" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  if (!proj) return <div className="text-sm text-[#94A3B8]">Loading…</div>;

  const stageIdx = STAGE_SEQ.indexOf(proj.status || "ordered");
  const cardCls = "card-pb p-5";

  return (
    <div data-testid="admin-project-detail" className="space-y-6">
      <Link to="/admin/projects" className="text-xs text-[#C9A356] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All projects</Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">{proj.project_number || proj.project_id}</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">{proj.title}</h1>
          <div className="text-sm text-[#475569] dark:text-slate-400 mt-1">{proj.customer_number || proj.customer_code} · {proj.project_type?.replace(/_/g, " ")}</div>
        </div>
        <HealthBadge projectId={id} />
      </div>

      {/* Stage stepper */}
      <div className={cardCls}>
        <div className="flex items-center gap-1 overflow-x-auto">
          {STAGE_SEQ.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex flex-col items-center min-w-[80px] ${i === stageIdx ? "opacity-100" : "opacity-60"}`} data-testid={`stage-${s}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center ${
                  i < stageIdx ? "bg-emerald-500 text-white" :
                  i === stageIdx ? "bg-[#C9A356] text-white ring-4 ring-[#C9A356]/20" :
                  "bg-slate-200 dark:bg-slate-700 text-[#475569]"
                }`}>
                  {i < stageIdx ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-xs">{i + 1}</span>}
                </div>
                <div className="text-[10px] mt-1 text-center text-[#475569] dark:text-slate-400">{STAGE_LABELS[s]}</div>
              </div>
              {i < STAGE_SEQ.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-[#475569] dark:text-slate-400">Current:</span>{" "}
            <span className="font-medium text-[#0A2540] dark:text-white">{STAGE_LABELS[proj.status] || proj.status}</span>{" "}
            <span className="text-[#94A3B8]">({proj.progress_pct || 0}%)</span>
          </div>
          {stageIdx < STAGE_SEQ.length - 1 && (
            <button onClick={advance} className="btn-accent text-sm" data-testid="advance-stage">
              Advance to {STAGE_LABELS[STAGE_SEQ[stageIdx + 1]]} <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          )}
          {proj.status === "completed" && (
            <Link to={`/admin/projects/${id}/handoff`} className="btn-primary text-sm" data-testid="open-handoff">
              <FileDown className="w-4 h-4 mr-1" /> Handoff report
            </Link>
          )}
        </div>
      </div>

      {/* Two-column grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Checklist */}
        <div className={cardCls}>
          <div className="inline-flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-[#C9A356]" />
            <p className="overline">Stage checklist ({STAGE_LABELS[proj.status]})</p>
          </div>
          <ul className="space-y-2">
            {checklist.map((it) => (
              <li key={it.key} className="flex items-start gap-2" data-testid={`chk-${it.key}`}>
                <button onClick={() => tick(it.key, !it.done)} className="shrink-0 mt-0.5" data-testid={`chk-toggle-${it.key}`}>
                  {it.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-[#94A3B8]" />}
                </button>
                <div className="flex-1">
                  <div className={`text-sm ${it.done ? "line-through text-[#94A3B8]" : "text-[#0A2540] dark:text-white"}`}>
                    {it.label} {!it.required && <span className="text-[10px] text-[#94A3B8]">(optional)</span>}
                  </div>
                  {it.done_at && <div className="text-[10px] text-[#94A3B8]">Ticked {new Date(it.done_at).toLocaleString()}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Crew */}
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#C9A356]" />
              <p className="overline">Crew ({(proj.crew || []).length})</p>
            </div>
            <button onClick={() => setShowAssign(true)} className="text-xs text-[#C9A356] hover:underline" data-testid="assign-crew-btn">+ Assign</button>
          </div>
          {(proj.crew || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">No crew yet. Assign at least one before scheduling.</p>
          ) : (
            <ul className="space-y-1">
              {(proj.crew || []).map((c) => (
                <li key={c.user_id} className="text-sm flex items-center justify-between border-b border-[#E2E8F0] dark:border-slate-700 py-1.5">
                  <span className="text-[#0A2540] dark:text-white">{c.name} <span className="text-[10px] text-[#94A3B8]">· {c.role}</span></span>
                  <button onClick={() => removeCrew(c.user_id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Materials */}
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <Package className="w-4 h-4 text-[#C9A356]" />
              <p className="overline">Materials reserved ({(proj.materials_reserved || []).length})</p>
            </div>
            <button onClick={() => setShowReserve(true)} className="text-xs text-[#C9A356] hover:underline" data-testid="reserve-mat-btn">+ Reserve</button>
          </div>
          {(proj.materials_reserved || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">No materials reserved.</p>
          ) : (
            <ul className="space-y-1">
              {(proj.materials_reserved || []).map((r) => (
                <li key={r.reservation_id} className="text-sm flex items-center justify-between border-b border-[#E2E8F0] dark:border-slate-700 py-1.5">
                  <span className="text-[#0A2540] dark:text-white">{r.material_name} <span className="text-[10px] text-[#94A3B8]">x{r.qty}</span></span>
                  <button onClick={() => releaseMat(r.reservation_id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Punchlist */}
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-[#C9A356]" />
              <p className="overline">Punchlist ({punchlist.filter(p => p.status !== "closed").length} open)</p>
            </div>
            <button onClick={() => setShowPunch(true)} className="text-xs text-[#C9A356] hover:underline" data-testid="add-punch-btn">+ Add</button>
          </div>
          {punchlist.length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">Clean punchlist ✓</p>
          ) : (
            <ul className="space-y-2">
              {punchlist.map((p) => (
                <li key={p.punch_id} className="border-b border-[#E2E8F0] dark:border-slate-700 py-2" data-testid={`punch-${p.punch_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className={`text-sm text-[#0A2540] dark:text-white ${p.status === "closed" ? "line-through opacity-60" : ""}`}>{p.description}</div>
                      <div className="text-[10px] mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded ${p.priority === "high" ? "bg-red-100 text-red-700" : p.priority === "low" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>{p.priority}</span>
                        <span className="ml-1.5 text-[#94A3B8] uppercase font-bold">{p.status}</span>
                        {(p.photos || []).length > 0 && (
                          <span className="ml-1.5 text-[#94A3B8]">· {(p.photos || []).length} photo{(p.photos || []).length === 1 ? "" : "s"}</span>
                        )}
                      </div>
                      {(p.photos || []).length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap" data-testid={`punch-photos-${p.punch_id}`}>
                          {(p.photos || []).map((ph) => (
                            <div key={ph.photo_id} className="flex flex-col items-start">
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}?auth=${encodeURIComponent(getToken() || "")}`}
                                alt={ph.caption || "remediation"}
                                className="w-12 h-12 object-cover rounded border border-[#E2E8F0] dark:border-slate-700"
                              />
                              {ph.caption && (
                                <span
                                  className="text-[9px] text-[#475569] dark:text-slate-400 max-w-[48px] leading-tight mt-0.5 line-clamp-2"
                                  title={ph.caption}
                                >
                                  {ph.caption}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => setPhotoPunch(p)}
                        className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5"
                        data-testid={`punch-photos-btn-${p.punch_id}`}
                      >
                        <Camera className="w-3 h-3" /> Photos
                      </button>
                      {p.status !== "closed" && (
                        <button onClick={() => closePunch(p.punch_id)} className="text-xs text-emerald-600 hover:underline" data-testid={`close-punch-${p.punch_id}`}>Close</button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Warranty */}
        {warranty && (
          <div className={`${cardCls} lg:col-span-2 border-l-4 border-emerald-500`}>
            <div className="inline-flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <p className="overline">Warranty active</p>
            </div>
            <div className="text-sm text-[#0A2540] dark:text-white">
              {warranty.years}-year warranty · registered {new Date(warranty.starts_at).toLocaleDateString()} · expires <b>{new Date(warranty.expires_at).toLocaleDateString()}</b>
            </div>
          </div>
        )}
      </div>

      {/* Project profitability */}
      <ProfitabilityCard projectId={id} />

      {/* Client Messages — per-project chat thread */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className={`${cardCls} lg:col-span-1`}>
          <div className="inline-flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[#C9A356]" />
            <p className="overline">Client conversation</p>
          </div>
          <p className="text-sm text-[#475569] dark:text-slate-400 leading-relaxed">
            Direct message thread with <span className="font-medium text-[#0A2540] dark:text-white">{proj.client_name || "the client"}</span>.
            Employees and other clients cannot see this thread.
          </p>
          <div className="mt-3 space-y-1 text-xs text-[#475569] dark:text-slate-400">
            <div>• Photos & PDFs (max 10 MB each, 8 per message)</div>
            <div>• Real-time via secure WebSocket</div>
            <div>• Every send is audit-logged</div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <ProjectChatPanel
            projectId={id}
            title={proj.client_name ? `${proj.client_name} · ${proj.customer_number || proj.customer_code || ""}` : "Client conversation"}
            subtitle={proj.title}
            height="h-[62vh]"
          />
        </div>
      </div>

      {/* Modals */}
      {showAssign && <AssignModal projectId={id} onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); load(); }} />}
      {showReserve && <ReserveModal projectId={id} materials={materials} onClose={() => setShowReserve(false)} onSaved={() => { setShowReserve(false); load(); }} />}
      {showPunch && <PunchModal projectId={id} onClose={() => setShowPunch(false)} onSaved={() => { setShowPunch(false); load(); }} />}
      {photoPunch && <PunchPhotoModal punch={photoPunch} onClose={() => setPhotoPunch(null)} onSaved={() => { setPhotoPunch(null); load(); }} />}
    </div>
  );
}

function AssignModal({ projectId, onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data)); }, []);
  const assign = async (uid) => {
    try { await api.post(`/projects/${projectId}/crew`, { user_id: uid }); toast.success("Assigned"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };
  return (
    <ModalShell title="Assign crew" onClose={onClose}>
      <ul className="max-h-[60vh] overflow-y-auto divide-y divide-[#E2E8F0] dark:divide-slate-700">
        {emps.map((e) => (
          <li key={e.user_id} className="py-2 flex items-center justify-between">
            <span className="text-sm text-[#0A2540] dark:text-white">{e.name} <span className="text-[10px] font-mono text-[#94A3B8]">{e.employee_code}</span></span>
            <button onClick={() => assign(e.user_id)} className="text-xs text-[#C9A356] hover:underline" data-testid={`pick-crew-${e.user_id}`}>Assign</button>
          </li>
        ))}
      </ul>
    </ModalShell>
  );
}

function ReserveModal({ projectId, materials, onClose, onSaved }) {
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);   // material row
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);

  const CATS = [
    { key: "all", label: "All" },
    { key: "panel", label: "Panels" },
    { key: "inverter", label: "Inverters" },
    { key: "battery", label: "Batteries" },
    { key: "wiring", label: "Wiring" },
    { key: "mounting", label: "Mounting" },
    { key: "general", label: "General" },
  ];

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (materials || []).filter((m) => {
      if (category !== "all" && (m.category || "general") !== category) return false;
      if (query) {
        const hay = `${m.name || ""} ${m.sku || ""} ${m.vendor || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [materials, category, q]);

  const available = picked ? Number(picked.stock || 0) - Number(picked.reserved || 0) : 0;
  const parsedQty = parseInt(qty, 10) || 0;
  const costImpact = picked ? parsedQty * (Number(picked.cost_usd) || 0) : 0;
  const canReserve = picked && parsedQty > 0 && parsedQty <= available;

  const save = async () => {
    if (!canReserve) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/materials`, {
        material_id: picked.material_id,
        qty: parsedQty,
      });
      toast.success(`Reserved ${parsedQty} × ${picked.name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="reserve-material-modal"
    >
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[86vh] flex flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E2E8F0] dark:border-slate-700">
          <div>
            <p className="overline">Inventory</p>
            <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">Materials needed for this project</h3>
            <p className="text-xs text-[#475569] dark:text-slate-400">Pick a category, then choose an item and quantity — stock is deducted from inventory.</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="reserve-close"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-[#475569] dark:text-slate-400 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Category chips */}
          <div className="flex flex-wrap gap-1.5">
            {CATS.map((c) => {
              const count = c.key === "all"
                ? (materials || []).length
                : (materials || []).filter((m) => (m.category || "general") === c.key).length;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => { setCategory(c.key); setPicked(null); }}
                  data-testid={`reserve-cat-${c.key}`}
                  className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition ${
                    category === c.key
                      ? "bg-[#0A2540] text-white"
                      : "bg-white dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 hover:border-[#0A2540] dark:hover:border-white"
                  }`}
                >
                  {c.label}
                  <span className={`text-[10px] rounded-full px-1.5 ${category === c.key ? "bg-white/20" : "bg-slate-100 dark:bg-slate-800"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, SKU, or vendor"
              data-testid="reserve-search"
              className="w-full h-10 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-3 text-sm"
            />
          </div>

          {/* Materials list */}
          <div className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 divide-y divide-[#E2E8F0] dark:divide-slate-800 max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="p-4 text-sm text-[#475569] dark:text-slate-400 text-center">
                No materials in this category match your search.
              </p>
            ) : (
              filtered.map((m) => {
                const avail = Number(m.stock || 0) - Number(m.reserved || 0);
                const isPicked = picked?.material_id === m.material_id;
                const outOfStock = avail <= 0;
                return (
                  <button
                    key={m.material_id}
                    type="button"
                    onClick={() => { if (!outOfStock) { setPicked(m); setQty(1); } }}
                    disabled={outOfStock}
                    data-testid={`reserve-pick-${m.material_id}`}
                    className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition ${
                      isPicked ? "bg-[#C9A356]/10 dark:bg-[#C9A356]/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    } ${outOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-[#0A2540] dark:text-white text-sm truncate">{m.name}</div>
                      <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                        {m.sku || "no SKU"} · ${(Number(m.cost_usd) || 0).toFixed(2)}/{m.unit || "ea"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-sm font-semibold tabular-nums ${outOfStock ? "text-red-500" : avail <= (m.reorder_level || 0) ? "text-amber-600" : "text-emerald-600"}`}>
                        {avail} avail
                      </div>
                      <div className="text-[10px] text-[#94A3B8]">of {m.stock} {m.unit || "ea"}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Quantity + summary */}
          {picked && (
            <div className="rounded-lg border-2 border-[#C9A356] bg-[#C9A356]/5 p-4" data-testid="reserve-summary">
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#C9A356] font-bold">Selected</div>
                  <div className="font-medium text-[#0A2540] dark:text-white text-sm truncate">{picked.name}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-xs text-[#475569] hover:text-[#0A2540] dark:text-slate-400 shrink-0"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-[#475569] dark:text-slate-400 shrink-0">Quantity</label>
                <div className="flex items-center gap-1.5 flex-1">
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))}
                    className="w-9 h-9 rounded-md bg-slate-100 dark:bg-slate-800 text-[#0A2540] dark:text-white"
                    aria-label="Decrease"
                  >−</button>
                  <input
                    type="number" min="1" max={available}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    data-testid="reserve-qty"
                    className="w-24 h-9 rounded-md border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white px-2 text-center text-sm tabular-nums"
                  />
                  <button
                    type="button"
                    onClick={() => setQty((q) => Math.min(available, (parseInt(q, 10) || 0) + 1))}
                    className="w-9 h-9 rounded-md bg-slate-100 dark:bg-slate-800 text-[#0A2540] dark:text-white"
                    aria-label="Increase"
                  >+</button>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase text-[#94A3B8]">Cost impact</div>
                  <div className="text-sm font-bold text-[#0A2540] dark:text-white tabular-nums">
                    ${costImpact.toFixed(2)}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-[#475569] dark:text-slate-400">
                Available: <strong className="text-[#0A2540] dark:text-white">{available}</strong> · Reserving {parsedQty} leaves {available - parsedQty} in stock.
                {parsedQty > available && (
                  <span className="ml-1 text-red-600 dark:text-red-400 font-medium">
                    Exceeds available stock.
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <footer className="p-5 border-t border-[#E2E8F0] dark:border-slate-700 flex flex-col sm:flex-row-reverse gap-2">
          <button
            onClick={save}
            disabled={!canReserve || saving}
            data-testid="reserve-save"
            className="h-11 px-5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Reserving…" : picked ? `Reserve ${parsedQty} × ${picked.name}` : "Choose a material"}
          </button>
          <button
            onClick={onClose}
            className="h-11 px-5 rounded-md border border-[#E2E8F0] dark:border-slate-700 hover:border-[#0A2540] dark:hover:border-white text-[#475569] dark:text-slate-300 font-semibold text-sm"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}

function PunchModal({ projectId, onClose, onSaved }) {
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const save = async () => {
    if (!desc.trim()) { toast.error("Description required"); return; }
    try { await api.post(`/projects/${projectId}/punchlist`, { description: desc, priority }); toast.success("Added"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };
  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm";
  return (
    <ModalShell title="Add punchlist item" onClose={onClose}>
      <div className="space-y-3">
        <textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What needs remediation?" className={cls} data-testid="punch-desc" />
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className={cls}>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <button onClick={save} className="btn-accent text-sm w-full" data-testid="punch-save">Add</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PunchPhotoModal({ punch, onClose, onSaved }) {
  const [photos, setPhotos] = useState(punch.photos || []);
  const [staged, setStaged] = useState([]); // newly uploaded, not yet attached
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  const attach = async () => {
    if (!staged.length) { onSaved(); return; }
    setSaving(true);
    try {
      for (const s of staged) {
        await api.post(`/punchlist/${punch.punch_id}/photos`, {
          file_id: s.file_id, url: s.url, caption: caption.trim() || null,
        });
      }
      toast.success(`Attached ${staged.length} photo${staged.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally {
      setSaving(false);
    }
  };

  const removeExisting = async (photo_id) => {
    if (!confirm("Remove this photo?")) return;
    try {
      await api.delete(`/punchlist/${punch.punch_id}/photos/${photo_id}`);
      setPhotos((arr) => arr.filter((p) => p.photo_id !== photo_id));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const token = getToken() || "";
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <ModalShell title="Punchlist photos" onClose={onClose}>
      <div className="text-xs text-[#475569] dark:text-slate-400 mb-3 border-l-2 border-[#C9A356] pl-2">
        {punch.description}
      </div>

      {photos.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-2">
            Attached ({photos.length})
          </p>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((ph) => (
              <div key={ph.photo_id} className="relative group" data-testid={`existing-photo-${ph.photo_id}`}>
                <img
                  src={`${backend}${ph.url}?auth=${encodeURIComponent(token)}`}
                  alt={ph.caption || "remediation"}
                  className="w-full aspect-square object-cover rounded border border-[#E2E8F0] dark:border-slate-700"
                />
                {ph.caption && (
                  <div className="text-[10px] mt-1 text-[#475569] dark:text-slate-400 line-clamp-2" title={ph.caption} data-testid={`photo-caption-${ph.photo_id}`}>
                    {ph.caption}
                  </div>
                )}
                <button
                  onClick={() => removeExisting(ph.photo_id)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"
                  data-testid={`remove-photo-${ph.photo_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-2">
        Add new
      </p>
      <PhotoUploader photos={staged} onChange={setStaged} max={6} label="Snap or select up to 6 photos" />

      {staged.length > 0 && (
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">
            Caption (optional — applies to all photos in this batch)
          </label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 200))}
            placeholder="e.g. Resealed roof penetration under panel 3 — 12 Feb"
            maxLength={200}
            className="w-full mt-1 border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm"
            data-testid="punch-photo-caption"
          />
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onClose} className="text-sm text-[#475569] px-3 py-1.5" data-testid="punch-photo-cancel">
          Close
        </button>
        <button
          onClick={attach}
          disabled={saving || staged.length === 0}
          className="btn-accent text-sm disabled:opacity-50"
          data-testid="punch-photo-save"
        >
          {saving ? "Saving…" : `Attach ${staged.length || ""}`.trim()}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- Project Health badge ----------
const HEALTH_TONES = {
  on_track:  { label: "On Track",   cls: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "bg-emerald-500" },
  attention: { label: "Attention",  cls: "bg-amber-100 text-amber-700 border-amber-300",       dot: "bg-amber-500" },
  delayed:   { label: "Delayed",    cls: "bg-red-100 text-red-700 border-red-300",             dot: "bg-red-500" },
  blocked:   { label: "Blocked",    cls: "bg-slate-800 text-white border-slate-700",           dot: "bg-white" },
};

function HealthBadge({ projectId }) {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [ovStatus, setOvStatus] = useState("attention");
  const [ovReason, setOvReason] = useState("");

  const load = () => api.get(`/projects/${projectId}/health`).then((r) => setHealth(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  if (!health) return null;
  const tone = HEALTH_TONES[health.status] || HEALTH_TONES.on_track;

  const applyOverride = async () => {
    if (!ovReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/projects/${projectId}/health/override`, { status: ovStatus, reason: ovReason.trim() });
      setOverrideMode(false); setOvReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const clearOverride = async () => {
    await api.post(`/projects/${projectId}/health/override`, { status: "", reason: "" });
    setOpen(false); setOverrideMode(false);
    load();
  };

  return (
    <div className="relative" data-testid="health-badge">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 border px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${tone.cls}`}
      >
        <span className={`w-2 h-2 rounded-full ${tone.dot} animate-pulse`}></span>
        {tone.label}
        {health.source === "manual_override" && <Settings2 className="w-3 h-3 opacity-70" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 card-pb p-4 z-20 shadow-lg" data-testid="health-panel">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-[#C9A356]" />
            <span className="font-heading text-sm text-[#0A2540] dark:text-white">Project health signals</span>
          </div>
          <p className="text-xs text-[#475569] dark:text-slate-400 mb-3">
            {health.reason}
            {health.source === "manual_override" && (
              <span className="block mt-1 text-amber-600 dark:text-amber-400">Manual override — set by admin</span>
            )}
          </p>
          {(health.signals || []).length > 0 && (
            <ul className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
              {health.signals.map((s, i) => {
                const t = HEALTH_TONES[s.severity] || HEALTH_TONES.attention;
                return (
                  <li key={i} className="text-[11px] flex items-start gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${t.dot}`}></span>
                    <span className="text-[#0A2540] dark:text-slate-200">{s.message}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {!overrideMode ? (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#E2E8F0] dark:border-slate-700">
              <button onClick={() => setOverrideMode(true)} className="text-xs text-[#C9A356] hover:underline flex-1 text-left" data-testid="health-override-open">
                Override status manually
              </button>
              {health.source === "manual_override" && (
                <button onClick={clearOverride} className="text-xs text-[#475569] hover:text-red-500" data-testid="health-clear-override">
                  Clear
                </button>
              )}
            </div>
          ) : (
            <div className="mt-2 pt-2 border-t border-[#E2E8F0] dark:border-slate-700 space-y-2">
              <select value={ovStatus} onChange={(e) => setOvStatus(e.target.value)}
                      data-testid="health-override-status"
                      className="w-full text-xs border border-[#E2E8F0] dark:border-slate-700 rounded px-2 py-1.5 bg-white dark:bg-slate-900">
                <option value="on_track">On Track</option>
                <option value="attention">Attention</option>
                <option value="delayed">Delayed</option>
                <option value="blocked">Blocked</option>
              </select>
              <textarea rows={2} value={ovReason} onChange={(e) => setOvReason(e.target.value)}
                        placeholder="Reason (required)…" maxLength={500}
                        data-testid="health-override-reason"
                        className="w-full text-xs border border-[#E2E8F0] dark:border-slate-700 rounded px-2 py-1.5 bg-white dark:bg-slate-900 outline-none focus:border-[#C9A356]" />
              <div className="flex gap-2">
                <button onClick={() => setOverrideMode(false)} className="text-xs px-2 py-1 text-[#475569] hover:text-[#0A2540]">
                  Cancel
                </button>
                <button onClick={applyOverride} data-testid="health-override-save" className="btn-accent text-xs px-3 py-1 ml-auto">
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Profitability Card ----------
function ProfitabilityCard({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/profitability`)
       .then((r) => setData(r.data))
       .catch((e) => setError(e?.response?.data?.detail || "Something went wrong — please try again."));
  }, [projectId]);
  if (error || !data) return null;
  const cur = data.currency;
  const marginTone = data.margin_delta_pct >= 0 ? "text-emerald-600" : "text-red-500";
  const MarginArrow = data.margin_delta_pct >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="card-pb p-5" data-testid="profitability-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#C9A356]" />
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">Project profitability</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">Contract value</div>
          <div className="text-2xl font-heading text-[#0A2540] dark:text-white tabular-nums">
            {fmtMoney(data.contract_value, cur)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <ProfitBlock title="Estimated" data={data.estimated} cur={cur} accent="border-slate-300" />
        <ProfitBlock title="Actual" data={data.actual} cur={cur} accent="border-[#C9A356]" hoursLine={data.actual.labor_hours} />
        <div className="rounded-xl border-2 border-dashed border-[#E2E8F0] dark:border-slate-700 p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">Margin delta</div>
          <div className={`text-3xl font-heading mt-1 tabular-nums inline-flex items-center gap-1 ${marginTone}`}>
            <MarginArrow className="w-5 h-5" />
            {data.margin_delta_pct > 0 ? "+" : ""}{data.margin_delta_pct}%
          </div>
          <div className="text-xs text-[#475569] dark:text-slate-400 mt-2">
            Est: <span className="tabular-nums">{data.estimated.margin_pct}%</span> · Act: <span className="tabular-nums">{data.actual.margin_pct}%</span>
          </div>
          <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-3">
            Est. profit: <b className="tabular-nums">{fmtMoney(data.estimated.gross_profit, cur)}</b>
          </div>
          <div className="text-[11px] text-[#475569] dark:text-slate-400">
            Act. profit: <b className="tabular-nums">{fmtMoney(data.actual.gross_profit, cur)}</b>
          </div>
        </div>
      </div>

      {data.materials_breakdown?.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-xs text-[#C9A356] font-semibold uppercase tracking-wider">
            Material cost breakdown ({data.materials_breakdown.length})
          </summary>
          <table className="w-full text-xs mt-2">
            <thead>
              <tr className="text-left text-[#94A3B8] uppercase tracking-wider">
                <th className="py-1.5">Item</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit cost</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.materials_breakdown.map((m) => (
                <tr key={m.name} className="border-t border-[#E2E8F0] dark:border-slate-800">
                  <td className="py-1.5 text-[#0A2540] dark:text-white">{m.name}</td>
                  <td className="text-right tabular-nums">{m.qty}</td>
                  <td className="text-right tabular-nums">{fmtMoney(m.unit_cost, cur)}</td>
                  <td className="text-right tabular-nums font-medium">{fmtMoney(m.total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function ProfitBlock({ title, data, cur, accent, hoursLine }) {
  const rows = [
    ["Materials", data.materials],
    ["Labor", data.labor],
    ["Subcontractors", data.subcontractors],
    ["Transport", data.transport],
    ["Other", data.other],
  ].filter(([_, v]) => v > 0);
  return (
    <div className={`rounded-xl border-2 ${accent} p-4`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{title} cost</div>
      <div className="text-2xl font-heading mt-1 text-[#0A2540] dark:text-white tabular-nums">
        {fmtMoney(data.total, cur)}
      </div>
      {hoursLine !== undefined && (
        <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-1">
          Labor: {hoursLine}h logged
        </div>
      )}
      <ul className="mt-3 space-y-1 text-xs">
        {rows.length === 0 ? (
          <li className="text-[#94A3B8] italic">No costs recorded yet</li>
        ) : rows.map(([k, v]) => (
          <li key={k} className="flex justify-between">
            <span className="text-[#475569] dark:text-slate-400">{k}</span>
            <span className="tabular-nums text-[#0A2540] dark:text-white">{fmtMoney(v, cur)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

